import axios from 'axios'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'

import statesAndCities from '@/assets/statesAndCities.json'
import Divider from '@/components/Divider'
import { maskCpf, maskPhone, unMask } from '@/components/Mask'
import Pagination from '@/components/Table/TablePagination'
import { useFetch } from '@/hooks/useFetch'
import PdfService from '@/pdf/PdfService'
import ApiService from '@/services/Api'
import { StreamingPartner } from '@/types/streamingPartner'
import { streamingPartners } from '@/utils/streamingPartners'
import {
    EditOutlined as EditIcon,
    AspectRatioOutlined as AccessCompanyIcon,
    DeleteOutlineOutlined as DeleteIcon,
    CloseOutlined as CloseIcon,
    PictureAsPdfOutlined as PdfIcon
} from '@mui/icons-material'
import WifiTetheringIcon from '@mui/icons-material/WifiTethering'
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogActions,
    CircularProgress,
    IconButton,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Link,
    Box,
    useTheme,
    Chip,
    DialogContent,
    Typography
} from '@mui/material'

import useStyles from './styles'
import CitySearchSelect from '@/components/CitySearchSelect'
import PasswordField from '@/components/PasswordField/PasswordField'
import { subDays, differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

export type ProductType = 'codec' | 'metrics' | 'intranet'
export type CodecRoleType = 'studio' | 'communicator'
export type IntranetRoleType = 'admin'

export type UserType = {
    uuid: string
    companyUuid: string
    name: string
    email: string
    password: string
    product: ProductType
    role: CodecRoleType | IntranetRoleType
    isBetaTester: boolean
    createdAt: Date
    updatedAt: Date
}

type IcecastType = {
    uuid?: string;
    companyUuid?: string;
    streamId?: number;
    currentBitrate?: number;
    currentFrequency?: number;
    currentFormat?: string;
    currentCodec?: string;
    dockerImage?: string;
    streamServerURL?: string;
    streamServerPort?: string;
    mountpointUsername?: string;
    mountpointPassword?: string;
    mountpointPort?: string;
    mountpointName?: string;
    adminUsername?: string;
    adminPassword?: string;
    streamingPartner?: StreamingPartner
    createdAt?: Date;
    updatedAt?: Date;
};

export type NoticeType = {
    uuid?: string
    userUuid?: string
    message?: string
    startDate?: Date | undefined
    endDate?: Date | undefined
    showAs?: string
    status?: boolean | unknown
    createdAt?: Date
    updatedAt?: Date
}

type AddressType = {
    uuid?: string
    country?: string
    state?: string
    city?: string
    neighborhood?: string
    street?: string
    number?: string
    complement?: string
    latitude?: string | null
    longitude?: string | null
    zipcode?: string
}

export type CompanyType = {
    campaignDialMonthLimit?: number | null
    uuid?: string
    streamId?: number
    addressUuid?: string

    frequency?: string
    externalIcecastUrl?: string

    name?: string
    corporateName?: string
    cnpjCpf?: string
    fistel?: string
    email?: string
    payerEmail?: string
    phoneNumber?: string
    payerPhoneNumber?: string
    logoBase64?: string | null
    logoUrl?: string
    site?: string;
    twitterLink?: string;
    instagramLink?: string;
    facebookLink?: string;
    youtubeLink?: string;
    publicWhatsappPhoneNumber?: string;
    aboutContent?: string
    mobileAppUuid?: string;

    streamPassword?: string

    createdAt?: Date
    updatedAt?: Date
    deletedAt?: Date
    trialExpireAt?: Date

    whatsappEnabled?: boolean
    whatsappHasSuspended?: boolean
    concurrenceEnabled?: boolean
    enabled?: boolean

    anydeskPassword?: string
    anydeskCode?: string
    supportPhonenumber?: string

    address?: AddressType
    users?: UserType[]
    icecast?: IcecastType
    notices?: NoticeType[]
    segments?: string[]

    streamingServerUrl?: string

    coveredCities?: string[]
    mapsZoom?: number
    status?: 'trial' | 'internal' | 'customer' | 'concurrence' | 'expired' | 'deleted'

    rhRadioUuid?: string
    rhRecorderUuid?: string

    adsolutionIds?: string[] | null

    referralPartner?: 'ACERT' | 'ADR' | 'Loopert'
    cnpjBilling?: string
    emailOpec?: string
    emailCommercial?: string
    whatsappOpec?: string
}

const Companies: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const { enqueueSnackbar } = useSnackbar()

    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [company, setCompany] = useState<CompanyType>({})
    const [icecast, setIcecast] = useState<IcecastType>()
    const [companyUuid, setCompanyUuid] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')
    const [openedCompanyDialog, setOpenedCompanyDialog] = useState<boolean>(false)
    const [openedIcecastDialog, setOpenedIcecastDialog] = useState<boolean>(false)
    const [openedCompanyDelete, setOpenedCompanyDelete] = useState<boolean>(false)
    const [loadingCompany, setLoadingCompany] = useState<boolean>(false)
    const [deletingCompany, setDeletingCompany] = useState<boolean>(false)
    const [searchInput, setSearchInput] = useState<string>('')
    const [filterEnabled, setFilterEnabledInput] = useState<boolean>(false)
    const [uploadedLogo, setUploadedLogo] = useState<File | undefined>()
    const [filterType, setFilterType] = useState<'clients' | 'competitors' | undefined>(undefined)
    const [selectedPartner, setSelectedPartner] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [openedAudioInfoDialog, setOpenedAudioInfoDialog] = useState<boolean>(false)
    const [detectedAudioInfo, setDetectedAudioInfo] = useState<any>(null)
    const [loadingAudioInfo, setLoadingAudioInfo] = useState<boolean>(false)
    const [loadingReportPdf, setLoadingReportPdf] = useState<boolean>(false)
    const VALID_FORMATS = ['aac_low', 'aac_he', 'aac_he_v2'];
    const VALID_BITRATES = [32, 40, 48, 56, 64, 80, 96, 112, 128];
    const VALID_FREQUENCIES = [44100, 48000];

    const isValidFormat = (format: string) => VALID_FORMATS.includes(format);
    const isValidBitrate = (bitrate: number) => VALID_BITRATES.includes(bitrate);
    const isValidFrequency = (frequency: number) => VALID_FREQUENCIES.includes(frequency);

    const [filterTrial, setFilterTrial] = useState<boolean>(false)
    const [filterExpired, setFilterExpired] = useState<boolean>(false)


    // 2. MODIFIQUE a função fetchAudioInfo para incluir o debug:

    const fetchAudioInfo = async (): Promise<void> => {
        if (!icecast?.streamServerURL || !icecast?.streamServerPort || !icecast?.mountpointName) {
            enqueueSnackbar('URL do servidor, porta e mount point são obrigatórios para buscar dados do áudio.', { variant: 'warning' })
            return
        }

        try {
            setLoadingAudioInfo(true)
            const { data } = await ApiService.post('/icecast/audio-info', {
                stream_server_url: icecast.streamServerURL,
                server_port: icecast.streamServerPort,
                mount_point: icecast.mountpointName
            })


            setDetectedAudioInfo(data.audioInfo)
            setOpenedAudioInfoDialog(true)

        } catch (error: any) {
            const message = error.data?.message || `Não foi possível obter dados do stream ${icecast.streamServerURL}:${icecast.streamServerPort}${icecast.mountpointName}`
            enqueueSnackbar(message, { variant: 'error' })
        } finally {
            setLoadingAudioInfo(false)
        }
    }
    const applyDetectedAudioInfo = () => {
        if (detectedAudioInfo) {
            const filteredData: any = {};
            const skippedFields: string[] = [];

            // Validar e aplicar bitrate
            if (isValidBitrate(detectedAudioInfo.bitrate)) {
                filteredData.currentBitrate = detectedAudioInfo.bitrate;
            } else {
                skippedFields.push('Bitrate');
            }

            // Validar e aplicar frequência
            if (isValidFrequency(detectedAudioInfo.frequency)) {
                filteredData.currentFrequency = detectedAudioInfo.frequency;
            } else {
                skippedFields.push('Frequência');
            }

            // Validar e aplicar formato
            if (isValidFormat(detectedAudioInfo.format)) {
                filteredData.currentFormat = detectedAudioInfo.format;
            } else {
                skippedFields.push('Formato');
            }

            // Codec sempre é aplicado (não há validação específica)
            filteredData.currentCodec = detectedAudioInfo.codec;

            // Aplicar os dados válidos
            setIcecast({
                ...icecast,
                ...filteredData
            });

            // Mostrar mensagem de sucesso
            if (skippedFields.length === 0) {
                enqueueSnackbar('Todos os dados foram aplicados com sucesso!', { variant: 'success' });
            } else {
                enqueueSnackbar(
                    `Dados aplicados com sucesso! Os seguintes campos foram ignorados por não serem compatíveis: ${skippedFields.join(', ')}.`,
                    { variant: 'warning' }
                );
            }
        }
        setOpenedAudioInfoDialog(false);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0) // Voltar para a primeira página ao alterar o número de linhas
    }

    const normalizeText = (text: string) =>
        text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '')

    const filteredCompanies = companies.filter((company) => {
        if (!filterEnabled && !company?.enabled) return false

        // Filtro de Concorrentes: apenas status 'concurrence'
        if (filterType === 'competitors' && company.status !== 'concurrence') return false

        // Filtro de Clientes: apenas status 'customer' (pagantes)
        if (filterType === 'clients' && company.status !== 'customer') return false

        if (selectedPartner !== '' && company.icecast?.streamingPartner !== selectedPartner) return false

        // Filtro para período de avaliação
        if (filterTrial && company.status !== 'trial') return false

        // Filtro para expirados
        if (filterExpired && company.status !== 'expired') return false

        if (searchInput) {
            const search = normalizeText(searchInput)
            const nameMatch = normalizeText(company.name || '').includes(search)
            const streamIdMatch = normalizeText(String(company.streamId) || '').includes(search)
            const cityMatch = normalizeText(company.address?.city || '').includes(search)

            return nameMatch || streamIdMatch || cityMatch
        }

        return true
    })

    const toggleFilterType = (type: 'clients' | 'competitors') => {
        setFilterType((prevType) => (prevType === type ? undefined : type))
    }

    // Dados paginados
    const paginatedCompanies = rowsPerPage === -1
        ? filteredCompanies
        : filteredCompanies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)



    const formatMapping = {
        aac_low: 'AAC LOW/LC',
        aac_he: 'AAC HE',
        aac_he_v2: 'AAC HE V2',
        aac_lc: 'AAC LC'
    }

    const openCompanyDelete = (uuid: string, name: string) => {
        setCompanyUuid(uuid)
        setCompanyName(name)
        setOpenedCompanyDelete(true)
    }

    const openCompanyIcecast = (icecast?: IcecastType, company?: CompanyType) => {
        if (icecast) {
            setIcecast({
                ...icecast,
                companyUuid: company?.uuid ?? '',
                streamId: company?.streamId ?? 0,
                streamServerURL: icecast.streamServerURL ?? '',
                adminUsername: icecast.adminUsername ?? '',
                adminPassword: icecast.adminPassword ?? '',
                streamServerPort: icecast.streamServerPort ?? '',
                mountpointName: icecast.mountpointName ?? '',
                streamingPartner: icecast.streamingPartner ?? 'Loopert'
            })
        } else {
            setIcecast({
                uuid: '',
                companyUuid: company?.uuid ?? '',
                streamId: company?.streamId ?? 0,
                dockerImage: '',
                streamServerURL: '',
                streamServerPort: '',
                mountpointUsername: '',
                mountpointPassword: '',
                mountpointPort: '',
                mountpointName: '',
                adminUsername: '',
                adminPassword: '',
                streamingPartner: 'Loopert',
                createdAt: new Date(),
                updatedAt: new Date()
            })
        }

        setOpenedIcecastDialog(true)
    }

    const openCompanyDialog = (company?: CompanyType) => {
        const initialAdsolutionIds = company?.adsolutionIds ?? []
        setAdsolutionIds(initialAdsolutionIds)

        setCompany(company
            ? {
                ...company,
                whatsappHasSuspended: company.whatsappHasSuspended ?? false,
                trialExpireAt: company.trialExpireAt ? new Date(company.trialExpireAt) : undefined,
                adsolutionIds: initialAdsolutionIds
            }
            : {
                whatsappEnabled: true,
                whatsappHasSuspended: false,
                enabled: false,
                address: {
                    state: '',
                    city: '',
                    neighborhood: '',
                    street: '',
                    number: '',
                    complement: '',
                    zipcode: '',
                    latitude: null,
                    longitude: null,
                },
                coveredCities: [],
                adsolutionIds: []
            })
        setOpenedCompanyDialog(true)
        setUploadedLogo(undefined)
    }

    const handleCloseCompanyDialog = () => {
        setOpenedCompanyDialog(false)
    }

    const getCompanies = async () => {
        const { data } = await ApiService.get('/company')
        const normalizedData = (data || []).map((companyItem: CompanyType) => ({
            ...companyItem,
            adsolutionIds: companyItem.adsolutionIds ?? []
        }))
        setCompanies(normalizedData)
        if (normalizedData.length > 0) {
            setIcecast(normalizedData[0].icecast)
        }
    }

    const deleteCompany = async (companyUuid: string) => {
        setDeletingCompany(true)
        setCompanyUuid('')
        setCompanyName('')
        try {
            await ApiService.delete(`/company/${companyUuid}`)
            await getCompanies()

            setOpenedCompanyDelete(false)
            setDeletingCompany(false)
        } catch (error) {
            enqueueSnackbar(String(error || 'Ocorreu um erro ao tentar confirmar o codigo.'), { variant: 'error' })
            setOpenedCompanyDelete(false)
            setDeletingCompany(false)
        }
    }

    const saveCompany = async () => {
        if (!company?.coveredCities?.length) {
            enqueueSnackbar('É necessário selecionar ao menos uma cidade de atuação.', {
                variant: 'warning'
            })
            return
        }

        const validStatuses = ['trial', 'internal', 'customer', 'concurrence']
        if (!company?.status || !validStatuses.includes(company.status)) {
            enqueueSnackbar('É necessário selecionar um status válido para a empresa.', {
                variant: 'error'
            })
            return
        }

        try {
            const payload = {
                ...company,
                adsolutionIds,
                latitude: Number(company?.address?.latitude),
                longitude: Number(company?.address?.longitude),
                trialExpireAt: company?.trialExpireAt ? new Date(company.trialExpireAt).toISOString() : null
            };

            console.log('Payload enviado:', payload);
            console.log('trialExpireAt:', payload.trialExpireAt);

            setLoadingCompany(true);
            await ApiService.post('/company', payload);

            handleCloseCompanyDialog();
        } catch (e) {

            let errorMessage = 'Ocorreu um erro ao criar a rádio.'

            if (e && typeof e === 'object' && 'data' in e) {
                const errorData = (e as any).data
                // Se data é uma string, usa diretamente
                if (typeof errorData === 'string') {
                    errorMessage = errorData
                }
                // Se data é um objeto com message, usa a propriedade message
                else if (errorData && errorData.message) {
                    errorMessage = errorData.message
                }
            }

            enqueueSnackbar(errorMessage, {
                variant: 'error'
            })
        }

        getCompanies();
        setLoadingCompany(false);
    }

    const geocodeAddress = async (address: AddressType): Promise<{ latitude: string; longitude: string } | null> => {
        try {
            const addressString = `${address.street || ''} ${address.number || ''}, ${address.neighborhood || ''}, ${address.city || ''}, ${address.state || ''}, Brasil`.trim()

            const response = await axios.get('https://photon.komoot.io/api/', {
                params: {
                    q: addressString,
                    limit: 1
                }
            })

            console.log(response)

            if (response.data.features && response.data.features.length > 0) {
                const coords = response.data.features[0].geometry.coordinates
                return {
                    longitude: coords[0].toString(),
                    latitude: coords[1].toString()
                }
            }

            return null
        } catch (error) {
            console.error('Erro ao buscar coordenadas:', error)
            return null
        }
    }


    const handleZipCodeInput = async (zipcode: string) => {
        try {
            setCompany({ ...company, address: { ...company?.address, zipcode: zipcode } })

            if (zipcode.length === 8) {
                const { data } = await axios.get(`https://viacep.com.br/ws/${zipcode}/json/`)

                if (data && !data.erro) {
                    const state = _.find(statesAndCities, { initials: data.uf })?.name

                    const updatedAddress = {
                        ...company?.address,
                        zipcode: zipcode,
                        street: data.logradouro || company.address?.street,
                        neighborhood: data.bairro || company.address?.neighborhood,
                        state: state || company.address?.state,
                        city: data.localidade || company.address?.city,
                        number: company.address?.number || '',
                        latitude: company.address?.latitude || null,
                        longitude: company.address?.longitude || null
                    }

                    setCompany({
                        ...company,
                        address: updatedAddress
                    })

                    // Buscar coordenadas automaticamente se tiver endereço completo
                    if (data.localidade && state) {
                        const coords = await geocodeAddress(updatedAddress)
                        if (coords) {
                            setCompany({
                                ...company,
                                address: {
                                    ...updatedAddress,
                                    latitude: coords.latitude,
                                    longitude: coords.longitude
                                }
                            })
                            enqueueSnackbar('Coordenadas obtidas automaticamente!', { variant: 'success' })
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Erro ao buscar CEP:', e)
        }
    }

    useFetch<CompanyType[]>(
        '/company',
        (data: CompanyType[]) => {
            setCompanies(data)
        }
    )

    const saveOrUpdateIcecast = async (): Promise<void> => {
        if (!icecast) {
            enqueueSnackbar('Nenhum dado de Icecast fornecido.', { variant: 'error' })
            return
        }

        // Verificação única para os campos obrigatórios
        const requiredFields: { key: keyof typeof icecast, label: string }[] = [
            { key: 'streamServerURL', label: 'Stream Server URL' },
            { key: 'adminUsername', label: 'Admin Username' },
            { key: 'adminPassword', label: 'Admin Password' },
            { key: 'streamServerPort', label: 'Stream Server Port' },
            { key: 'mountpointName', label: 'Mountpoint Name' },
            { key: 'streamingPartner', label: 'Streaming Partner' }
        ]

        for (const field of requiredFields) {
            const value = icecast[field.key]
            if (value === undefined || value === null || value === '') {
                enqueueSnackbar(`O campo ${field.label} é obrigatório e não pode estar vazio.`, { variant: 'error' })
                return
            }
        }

        try {
            setLoadingCompany(true)
            await ApiService.post('/icecast', icecast)
            enqueueSnackbar('Icecast salvo com sucesso!', { variant: 'success' })
            getCompanies()
            setOpenedIcecastDialog(false)
        } catch (e: any) {
            const status = e.status;
            const message = e.data?.message || 'Ocorreu um erro ao salvar o Icecast';

            if (status === 409) {
                enqueueSnackbar(message, { variant: 'error' });
                return;
            }
            enqueueSnackbar('Ocorreu um erro ao salvar o Icecast', { variant: 'error' });
        } finally {
            setLoadingCompany(false)
        }
    }

    const handleSelectLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const file = event.target.files?.[0]
            if (!file) return

            const validFormats = ['image/png', 'image/jpeg', 'image/jpg']
            if (!validFormats.includes(file.type)) {
                enqueueSnackbar('Formato inválido. Apenas PNG, JPEG e JPG são permitidos.', { variant: 'warning' })
                return
            }

            const fileBase64: string = await new Promise((resolve) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onloadend = (evt) => {
                    if (evt?.target?.readyState === FileReader.DONE) {
                        resolve(evt?.target?.result as string)
                    }
                }
            })

            setCompany({ ...company, logoBase64: String(fileBase64) })
            setUploadedLogo(file)
        } catch (error) {
            enqueueSnackbar('Ocorreu um erro ao tentar fazer upload da imagem.', { variant: 'warning' })
        }
    }

    const handleRemoveLogo = (e: React.MouseEvent) => {
        e.stopPropagation()
        setUploadedLogo(undefined)
        setCompany({
            ...company,
            logoBase64: null,
            logoUrl: undefined
        })
    }

    const handleCityChange = (value: string[]) => {
        setCompany({
            ...company,
            coveredCities: value as string[],
        });
    };

    const handleStarClick = (value: string) => {
        const updatedCities = [value, ...company?.coveredCities?.filter((city) => city !== value) || []];
        setCompany({
            ...company,
            coveredCities: updatedCities,
        });
    };

    const [showSelection, setShowSelection] = useState(false);
    const [dateRange, setDateRange] = useState({
        start: subDays(new Date(), 7),
        end: subDays(new Date(), 1)
    });
    const executeReportGeneration = async () => {

        const daysDiff = differenceInDays(dateRange.end, dateRange.start) + 1;

        if (daysDiff > 8 || daysDiff <= 0) {
            enqueueSnackbar('O intervalo deve ser de 1 a 8 dias.', { variant: 'error' });
            return;
        }

        setShowSelection(false);
        setLoadingReportPdf(true);

        try {
            const params = {
                from: startOfDay(dateRange.start).toISOString(),
                to: endOfDay(dateRange.end).toISOString()
            };

            await PdfService.generateCoverAndOpen(companyUuid, params);
            enqueueSnackbar('Relatório gerado com sucesso!', { variant: 'success' });
        } catch (error: any) {
            const errorMessage = error?.message || 'Erro ao gerar PDF';
            enqueueSnackbar(errorMessage, {
                variant: error?.message.includes('Não há dados') ? 'warning' : 'error'
            });
        } finally {
            setLoadingReportPdf(false);
        }
    }

    const [inputValue, setInputValue] = useState('');
    const [adsolutionIds, setAdsolutionIds] = useState<string[]>([])
    const handleAdd = () => {
        const value = inputValue.trim();

        if (!value) return;

        const nextAdsolutionIds = [...adsolutionIds, value];
        setAdsolutionIds(nextAdsolutionIds);
        setCompany((prevCompany) => ({
            ...prevCompany,
            adsolutionIds: nextAdsolutionIds
        }));
        setInputValue('');
    };

    const handleDelete = (idToDelete: string) => {
        const nextAdsolutionIds = adsolutionIds.filter(id => id !== idToDelete);
        setAdsolutionIds(nextAdsolutionIds);
        setCompany((prevCompany) => ({
            ...prevCompany,
            adsolutionIds: nextAdsolutionIds
        }));
    }

    useEffect(() => {
        if (!company) return;

        if (company.adsolutionIds !== adsolutionIds) {
            setCompany({
                ...company,
                adsolutionIds: adsolutionIds
            });
        }
    }, [adsolutionIds]);
    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid>
                    <TextField
                        id="company_search_field"
                        label="Buscar empresa" size="small" color="primary" variant="outlined"
                        value={searchInput}
                        onChange={({ target }) => setSearchInput(target.value)}
                    />

                    <FormControlLabel
                        className={classes.filterEnabledCheckbox}
                        control={
                            <Checkbox
                                id="filter_enabled_checkbox"
                                checked={filterEnabled}
                                onChange={({ target }) => setFilterEnabledInput(target.checked)}
                                color="default"
                            />
                        }
                        label={'Filtrar inativos'}
                    />
                </Grid>
                <Button
                    id="create_radio_button"
                    color="primary"
                    variant="contained"
                    onClick={() => openCompanyDialog()}
                >
                    Criar nova rádio
                </Button>
            </Grid>

            <Divider size={3} />

            <Grid display="flex" gap={2} marginBottom={2} flexWrap="wrap">
                <Button
                    id="filter_all_button"
                    variant={filterType === undefined && selectedPartner === '' && !filterTrial && !filterExpired ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterType(undefined)
                        setSelectedPartner('')
                        setFilterTrial(false)
                        setFilterExpired(false)
                        setPage(0)
                    }}
                    size='small'
                >
                    Todos
                </Button>
                <Button
                    id="filter_competitors_button"
                    variant={filterType === 'competitors' ? 'contained' : 'outlined'}
                    onClick={() => { toggleFilterType('competitors'); setFilterTrial(false); setFilterExpired(false); setPage(0) }}
                    size='small'
                >
                    Concorrentes
                </Button>
                <Button
                    id="filter_clients_button"
                    variant={filterType === 'clients' ? 'contained' : 'outlined'}
                    onClick={() => { toggleFilterType('clients'); setFilterTrial(false); setFilterExpired(false); setPage(0) }}
                    size='small'
                >
                    Clientes
                </Button>
                <Button
                    id="filter_trial_button"
                    variant={filterTrial ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterTrial(prev => !prev)
                        // Se ativando o filtro trial, desativa os outros
                        if (!filterTrial) {
                            setFilterType(undefined)
                            setSelectedPartner('')
                            setFilterExpired(false)
                        }
                        setPage(0)
                    }}
                    size='small'
                >
                    Período de Avaliação
                </Button>

                <Button
                    id="filter_expired_button"
                    variant={filterExpired ? 'contained' : 'outlined'}
                    onClick={() => {
                        setFilterExpired(prev => !prev)
                        // desativa os outros filtros
                        if (!filterExpired) {
                            setFilterTrial(false)
                            setFilterType(undefined)
                            setSelectedPartner('')
                        }
                        setPage(0)
                    }}
                    size='small'
                >
                    Expirados
                </Button>

                <FormControl variant="outlined" className={classes.formControlSelect} size='small'>
                    <InputLabel id="streaming_partner_filter_label">Parceiro de streaming</InputLabel>
                    <Select
                        id="streaming_partner_filter_field"
                        value={selectedPartner}
                        defaultValue=''
                        onChange={(e) => {
                            setSelectedPartner(e.target.value); setFilterTrial(false); setPage(0)
                        }
                        }
                        variant="outlined"
                        color="primary"
                        label="Parceiro de streaming"
                        labelId="streaming_partner_filter_label"
                    >
                        <MenuItem value=''>Todos</MenuItem>
                        {streamingPartners.map((partner) => (
                            <MenuItem key={partner.value} value={partner.value}>{partner.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

            </Grid>
            <Grid container>
                <TableContainer component={Paper} className={classes.table}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell align="center">Cidade</TableCell>
                                <TableCell align="center">Frequência</TableCell>
                                <TableCell align="center">ID do adstream/icecast</TableCell>
                                <TableCell align="center">Bitrate Icecast</TableCell>
                                <TableCell align="center">Frequência Icecast</TableCell>
                                <TableCell align="center">Format</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                paginatedCompanies.map((company) => (
                                    <TableRow key={company.uuid}>
                                        <TableCell component="th" scope="row" className={classes.truncateCell}>{company.name}</TableCell>
                                        <TableCell align="center">{company?.address?.city}</TableCell>
                                        <TableCell align="center">{company?.frequency}</TableCell>
                                        <TableCell align="center">{company?.streamId}</TableCell>
                                        <TableCell align="center">{company?.icecast?.currentBitrate && `${company?.icecast?.currentBitrate}k`}</TableCell>
                                        <TableCell align="center">{company?.icecast?.currentFrequency}</TableCell>
                                        <TableCell align="center">{formatMapping[company?.icecast?.currentFormat as keyof typeof formatMapping] || company?.icecast?.currentFormat || ''}</TableCell>
                                        <TableCell align="center">
                                            {(() => {
                                                if (company?.status === 'expired') {
                                                    return (
                                                        <span style={{ color: '#f44336', fontWeight: 'bold' }}>
                                                            Expirado
                                                        </span>
                                                    )
                                                }
                                                return company?.status === 'trial' ? 'Período de Avaliação' :
                                                    company?.status === 'internal' ? 'Interna' :
                                                        company?.status === 'customer' ? 'Pagante' :
                                                            company?.status === 'concurrence' ? 'Concorrência' :
                                                                'Não definido'
                                            })()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => openCompanyIcecast(company?.icecast, company)}
                                                title={'Icecast'}
                                            >
                                                <WifiTetheringIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openCompanyDelete(company?.uuid || '', company?.name || 'esta rádio')}
                                                title='Deletar'
                                            >
                                                <DeleteIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openCompanyDialog(company)}
                                                title='Editar'
                                            >
                                                <EditIcon />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => {
                                                    setCompanyUuid(company?.uuid || '');
                                                    setShowSelection(true);
                                                }}
                                                title='Gerar Relatório'
                                            >
                                                <PdfIcon />
                                            </IconButton>
                                            <Link href={`/companies/${company.uuid}`}>
                                                <IconButton title='Acessar'>
                                                    <AccessCompanyIcon />
                                                </IconButton>
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                    <Pagination
                        count={filteredCompanies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </Grid>

            <Dialog open={openedCompanyDialog}>
                <DialogTitle className={classes.dialogTitle}>
                    {!company?.uuid ? 'Criando nova ' : 'Editando a '}rádio
                </DialogTitle>
                <Grid container className={classes.companyDialogContainer} >
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="company_name_field"
                            className={classes.itemInput}
                            label="Nome Fantasia" size="small" color="primary" variant="outlined"
                            value={company.name}
                            onChange={({ target }) => setCompany({ ...company, name: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="company_frequency_field"
                            className={classes.itemInput}
                            label="Frequência" size="small" color="primary" variant="outlined"
                            value={company.frequency}
                            onChange={({ target }) => setCompany({ ...company, frequency: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="radio_enabled_checkbox"
                                    checked={company.enabled}
                                    onChange={({ target }) => setCompany({ ...company, enabled: target.checked })}
                                    color="default"
                                />
                            }
                            label={'Radio Ativa'}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="concurrence_enabled_checkbox"
                                    checked={company.concurrenceEnabled}
                                    onChange={({ target }) => setCompany({ ...company, concurrenceEnabled: target.checked })}
                                    color="default"
                                />
                            }
                            label={'Concorrência Pública Ativa'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.selectInput} htmlFor='campaign_dial_limit'>Plano Campanhas Dial</InputLabel>
                            <Select
                                onChange={({ target }) => {
                                    setCompany({
                                        ...company,
                                        campaignDialMonthLimit: target.value === 'unlimited'
                                            ? null
                                            : (typeof target.value === 'number' && target.value > 0
                                                ? target.value
                                                : null)
                                    })
                                }}
                                id="campaign_dial_limit"
                                label="Plano campanhas dial"
                                variant="outlined"
                                color="primary"
                                className={classes.select}
                                value={company?.campaignDialMonthLimit === null ? 'unlimited' : company?.campaignDialMonthLimit}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="unlimited">Desativado</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={250}>250</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.selectInput} htmlFor='company_status_select'>Status da Empresa</InputLabel>
                            <Select
                                onChange={({ target }) => {
                                    const newStatus = target.value as 'trial' | 'internal' | 'customer' | 'concurrence'

                                    // Se mudou para trial e não tem data ainda, define +30 dias
                                    let newTrialExpireAt = company.trialExpireAt
                                    if (newStatus === 'trial' && !company.trialExpireAt) {
                                        const date30DaysFromNow = new Date()
                                        date30DaysFromNow.setDate(date30DaysFromNow.getDate() + 30)
                                        newTrialExpireAt = date30DaysFromNow
                                    } else if (newStatus !== 'trial' && newStatus !== 'customer') {
                                        // Limpa a data se não for trial ou customer
                                        newTrialExpireAt = undefined
                                    }

                                    setCompany({
                                        ...company,
                                        status: newStatus,
                                        trialExpireAt: newTrialExpireAt
                                    })
                                }}
                                id="company_status_select"
                                label="Status da Empresa"
                                variant="outlined"
                                color="primary"
                                className={classes.select}
                                value={company?.status || ''}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="trial">Período de Avaliação</MenuItem>
                                <MenuItem value="internal">Interna</MenuItem>
                                <MenuItem value="customer">Pagante</MenuItem>
                                <MenuItem value="concurrence">Concorrência</MenuItem>
                                <MenuItem value="expired" disabled>Expirado</MenuItem>
                                <MenuItem value="deleted" disabled>Deletado</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.selectInput} htmlFor='company_referral_partner_select'>Indicação de parceiro</InputLabel>
                            <Select
                                onChange={({ target }) => {
                                    setCompany({
                                        ...company,
                                        referralPartner: target.value as 'ACERT' | 'ADR' | 'Loopert'
                                    })
                                }}
                                id="company_referral_partner_select"
                                label="Indicação de parceiro"
                                variant="outlined"
                                color="primary"
                                className={classes.select}
                                value={company?.referralPartner || ''}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 400
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="Loopert">Loopert</MenuItem>
                                <MenuItem value="ACERT">ACERT</MenuItem>
                                <MenuItem value="ADR">ADR</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.selectInput} htmlFor='segments_select'>Segmentos</InputLabel>
                            <Select
                                onChange={({ target }) => {
                                    setCompany({
                                        ...company,
                                        segments: typeof target.value === 'string'
                                            ? [target.value]
                                            : target.value as string[]
                                    }
                                    )
                                }}
                                id="segments_select"
                                label="Segmentos"
                                variant="outlined"
                                color="primary"
                                multiple
                                className={classes.select}
                                value={company?.segments || []}
                                MenuProps={
                                    {
                                        PaperProps: {
                                            style: {
                                                maxHeight: 400
                                            }
                                        }
                                    }
                                }
                            >
                                <MenuItem value="Adulto Classic">Adulto Classic</MenuItem>
                                <MenuItem value="Adulto Contemporâneo">Adulto Contemporâneo</MenuItem>
                                <MenuItem value="Eclética">Eclética</MenuItem>
                                <MenuItem value="Esportes">Esportes</MenuItem>
                                <MenuItem value="Entrevistas">Entrevistas</MenuItem>
                                <MenuItem value="Flashback">Flashback</MenuItem>
                                <MenuItem value="Gospel Católica">Gospel Católica</MenuItem>
                                <MenuItem value="Gospel Evangélica">Gospel Evangélica</MenuItem>
                                <MenuItem value="Jornalismo (News)">Jornalismo (News)</MenuItem>
                                <MenuItem value="MPB">MPB</MenuItem>
                                <MenuItem value="Pop ">Pop </MenuItem>
                                <MenuItem value="Pop-Rock">Pop-Rock</MenuItem>
                                <MenuItem value="Sertanejo">Sertanejo</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* NOVO CAMPO - Aparece quando status é 'trial' ou 'customer' */}
                    {(company?.status === 'trial' || company?.status === 'customer') && (
                        <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                            <TextField
                                id="trial_expire_at_field"
                                className={classes.itemInput}
                                label={company?.status === 'trial' ? 'Data de Expiração do Trial' : 'Data de expiração da assinatura'}
                                type="date"
                                size="small"
                                color="primary"
                                variant="outlined"
                                value={
                                    company.trialExpireAt
                                        ? (company.trialExpireAt instanceof Date
                                            ? company.trialExpireAt.toISOString().split('T')[0]
                                            : new Date(company.trialExpireAt).toISOString().split('T')[0])
                                        : ''
                                }
                                onChange={({ target }) => {
                                    setCompany({
                                        ...company,
                                        trialExpireAt: target.value ? new Date(target.value + 'T00:00:00') : undefined
                                    })
                                }}
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={12} className={classes.dialogInputFile}
                        onClick={() => document.getElementById('logo-selector')?.click()}
                    >
                        <Button
                            fullWidth
                            color="primary"
                            variant="text"
                        >
                            ESCOLHER LOGO
                            {uploadedLogo ? ` (${uploadedLogo.name})` : ''}
                            <input
                                id="logo-selector"
                                type="file"
                                accept=".png, .jpeg, .jpg"
                                onChange={handleSelectLogo}
                                multiple={false}
                                style={{ display: 'none' }}
                            />
                        </Button>
                        {(uploadedLogo || company?.logoUrl) && (
                            <div className={classes.dialogLogo}>
                                <CloseIcon className={classes.closeIcon} onClick={(e) => handleRemoveLogo(e)} />
                                <img
                                    src={uploadedLogo ? URL.createObjectURL(uploadedLogo) : `${company?.logoUrl}?t=${Date.now()}`}
                                    alt="Logo Preview"
                                    className={classes.dialogLogoImg}
                                />
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <TextField
                            id="external_icecast_url_field"
                            className={classes.itemInput}
                            label="Link externo do Icecast (Concorrencia) (Ex. /status.xml)" size="small" color="primary" variant="outlined"
                            value={company.externalIcecastUrl}
                            onChange={({ target }) => setCompany({ ...company, externalIcecastUrl: target.value })}
                        />
                    </Grid>

                    <Divider size={2} />
                    <Box sx={{ width: '100%', height: '1px', backgroundColor: '#ccc', margin: '10px' }} />
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="rh_radio_uuid_field"
                            className={classes.itemInput}
                            label="UUID (Rádio Health)" size="small" color="primary" variant="outlined"
                            value={company.rhRadioUuid}
                            onChange={({ target }) => setCompany({ ...company, rhRadioUuid: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="rh_recorder_uuid_field"
                            className={classes.itemInput}
                            label="Recorder UUID (Rádio Health)" size="small" color="primary" variant="outlined"
                            value={company.rhRecorderUuid}
                            onChange={({ target }) => setCompany({ ...company, rhRecorderUuid: target.value })}
                        />
                    </Grid>

                    <Grid item xs={12} className={classes.dialogItemInput}>
                        <TextField
                            label="IDs do Adsolution"
                            placeholder="Digite Ex: 1236 e pressione Enter"
                            value={inputValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {
                                    setInputValue(value);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault();
                                    handleAdd();
                                }
                            }}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                            fullWidth
                            size="small"
                        />
                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                            {adsolutionIds.map((id, index) => (
                                <Chip
                                    key={index}
                                    label={id}
                                    onDelete={() => handleDelete(id)}


                                />
                            ))}
                        </Box>
                    </Grid>
                    <Box sx={{ width: '96%', height: '1px', backgroundColor: '#ccc', margin: '5px auto' }} />

                    <Divider size={2} />

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="corporate_name_field"
                            className={classes.itemInput}
                            label="Razão Social" size="small" color="primary" variant="outlined"
                            value={company.corporateName}
                            onChange={({ target }) => setCompany({ ...company, corporateName: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="cnpj_cpf_field"
                            className={classes.itemInput}
                            label="CNPJ/CPF da rádio" size="small" color="primary" variant="outlined"
                            value={maskCpf(company.cnpjCpf)}
                            onChange={({ target }) => setCompany({ ...company, cnpjCpf: unMask(target.value) })}
                            inputProps={{ maxLength: 18 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="cnpj_billing_field"
                            className={classes.itemInput}
                            label="CNPJ de Cobrança" size="small" color="primary" variant="outlined"
                            value={maskCpf(company.cnpjBilling)}
                            onChange={({ target }) => setCompany({ ...company, cnpjBilling: unMask(target.value) })}
                            inputProps={{ maxLength: 18 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="email_opec_field"
                            className={classes.itemInput}
                            label="Email OPEC" size="small" color="primary" variant="outlined"
                            value={company.emailOpec}
                            onChange={({ target }) => setCompany({ ...company, emailOpec: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="email_commercial_field"
                            className={classes.itemInput}
                            label="Email Comercial" size="small" color="primary" variant="outlined"
                            value={company.emailCommercial}
                            onChange={({ target }) => setCompany({ ...company, emailCommercial: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="whatsapp_opec_field"
                            className={classes.itemInput}
                            label="WhatsApp OPEC" size="small" color="primary" variant="outlined"
                            value={maskPhone(company.whatsappOpec)}
                            onChange={({ target }) => setCompany({ ...company, whatsappOpec: unMask(target.value) })}
                            inputProps={{
                                maxLength: 16,
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="fistel_field"
                            className={classes.itemInput}
                            label="Fistel" size="small" color="primary" variant="outlined"
                            value={company.fistel}
                            onChange={({ target }) => setCompany({ ...company, fistel: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="email_field"
                            className={classes.itemInput}
                            label="Email" size="small" color="primary" variant="outlined"
                            value={company.email}
                            onChange={({ target }) => setCompany({ ...company, email: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="payer_email_field"
                            className={classes.itemInput}
                            label="Email do financeiro" size="small" color="primary" variant="outlined"
                            value={company.payerEmail}
                            onChange={({ target }) => setCompany({ ...company, payerEmail: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="phone_number_field"
                            className={classes.itemInput}
                            label="Número de telefone" size="small" color="primary" variant="outlined"
                            value={maskPhone(company.phoneNumber)}
                            onChange={({ target }) => setCompany({ ...company, phoneNumber: unMask(target.value) })}
                            inputProps={{
                                maxLength: 16,
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}

                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="payer_phone_field"
                            className={classes.itemInput}
                            label="Número de telefone do financeiro" size="small" color="primary" variant="outlined"
                            value={maskPhone(company.payerPhoneNumber)}
                            onChange={({ target }) => setCompany({ ...company, payerPhoneNumber: unMask(target.value) })}
                            inputProps={{
                                maxLength: 16,
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="support_phone_field"
                            className={classes.itemInput}
                            label="Número de telefone do suporte" size="small" color="primary" variant="outlined"
                            value={maskPhone(company?.supportPhonenumber)}
                            onChange={({ target }) => setCompany({ ...company, supportPhonenumber: unMask(target.value) })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="anydesk_code_field"
                            className={classes.itemInput}
                            label="Código do Anydesk" size="small" color="primary" variant="outlined"
                            value={company?.anydeskCode}
                            onChange={({ target }) => setCompany({ ...company, anydeskCode: target.value })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="anydesk_password_field"
                            className={classes.itemInput}
                            label="Senha do Anydesk" size="small" color="primary" variant="outlined"
                            value={company?.anydeskPassword}
                            onChange={({ target }) => setCompany({ ...company, anydeskPassword: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="site_field"
                            className={classes.itemInput}
                            label="Site" size="small" color="primary" variant="outlined"
                            value={company.site}
                            placeholder="loopert.com"
                            onChange={({ target }) => setCompany({ ...company, site: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="twitter_field"
                            className={classes.itemInput}
                            label="Twitter" size="small" color="primary" variant="outlined"
                            value={company.twitterLink}
                            placeholder="Loopert Inc"
                            onChange={({ target }) => setCompany({ ...company, twitterLink: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="instagram_field"
                            className={classes.itemInput}
                            label="Instagram" size="small" color="primary" variant="outlined"
                            value={company.instagramLink}
                            placeholder="Loopert Inc"
                            onChange={({ target }) => setCompany({ ...company, instagramLink: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="facebook_field"
                            className={classes.itemInput}
                            label="Facebook" size="small" color="primary" variant="outlined"
                            value={company.facebookLink}
                            placeholder="Loopert Inc"
                            onChange={({ target }) => setCompany({ ...company, facebookLink: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="youtube_field"
                            className={classes.itemInput}
                            label="YouTube" size="small" color="primary" variant="outlined"
                            value={company.youtubeLink}
                            placeholder="Loopert Inc"
                            onChange={({ target }) => setCompany({ ...company, youtubeLink: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="whatsapp_field"
                            className={classes.itemInput}
                            label="Whatsapp da rádio" size="small" color="primary" variant="outlined"
                            value={maskPhone(company?.publicWhatsappPhoneNumber)}
                            onChange={({ target }) => setCompany({ ...company, publicWhatsappPhoneNumber: unMask(target.value) })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!company?.whatsappHasSuspended}
                                    onChange={({ target }) => setCompany({ ...company, whatsappHasSuspended: target.checked })}
                                    color="primary"
                                />
                            }
                            label="Já foi suspenso do Whatsapp"
                        />
                    </Grid>
                </Grid>

                <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                    <TextField
                        id="about_content_field"
                        type="textarea"
                        className={classes.itemInput}
                        label="Sobre a rádio" size="small" color="primary" variant="outlined"
                        value={company.aboutContent}
                        onChange={({ target }) => setCompany({ ...company, aboutContent: target.value })}
                        multiline rows={4}
                    />
                </Grid>

                <Grid container >
                    <Box sx={{ width: '100%', height: '1px', backgroundColor: '#ccc', margin: '10px' }} />

                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="zipcode_field"
                                className={classes.itemInput}
                                label="CEP" size="small" color="primary" variant="outlined"
                                value={company?.address?.zipcode}
                                placeholder="99999999"
                                onChange={({ target }) => handleZipCodeInput(String(target.value))}
                                inputProps={{
                                    'data-lpignore': 'true',
                                    'data-1password': 'true',
                                    'data-op-ignore': 'true'
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="state_select_label">Estado</InputLabel>
                            <Select
                                id="state_select_field"
                                value={company?.address?.state}
                                onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, city: '', state: String(target.value) } })}
                                variant="outlined"
                                color="primary"
                                label="Estado"
                                labelId="state_select_label"
                            >
                                {
                                    statesAndCities.map((state: any) => (
                                        <MenuItem key={state.name} value={state.name}>{state.name}</MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        {
                            company?.address?.state && (
                                <FormControl variant="outlined" className={classes.formControl} size='small'>
                                    <InputLabel id="city_select_label">Cidade</InputLabel>
                                    <Select
                                        id="city_select_field"
                                        value={company?.address?.city}
                                        onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, city: String(target.value) } })}
                                        variant="outlined"
                                        color="primary"
                                        label="Cidade"
                                        labelId="city_select_label"
                                    >
                                        {
                                            statesAndCities.filter(state => state.name === company?.address?.state)[0].cities.map((cityName: string) => (
                                                <MenuItem key={cityName} value={cityName}>{cityName}</MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            )
                        }
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="neighborhood_field"
                            className={classes.itemInput}
                            label="Bairro" size="small" color="primary" variant="outlined"
                            value={company?.address?.neighborhood}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, neighborhood: target.value } })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="street_field"
                            className={classes.itemInput}
                            label="Rua" size="small" color="primary" variant="outlined"
                            value={company?.address?.street}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, street: target.value } })}
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="number_field"
                            className={classes.itemInput}
                            label="Número" size="small" color="primary" variant="outlined"
                            value={company?.address?.number}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, number: target.value } })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="complement_field"
                            className={classes.itemInput}
                            label="Complemento" size="small" color="primary" variant="outlined"
                            value={company?.address?.complement}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, complement: target.value } })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="latitude_field"
                            className={classes.itemInput}
                            label="Latitude"
                            size="small"
                            color="primary"
                            variant="outlined"
                            value={company?.address?.latitude || ''}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, latitude: target.value } })}
                            InputLabelProps={{
                                shrink: !!company?.address?.latitude
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="longitude_field"
                            className={classes.itemInput}
                            label="Longitude"
                            size="small"
                            color="primary"
                            variant="outlined"
                            value={company?.address?.longitude || ''}
                            onChange={({ target }) => setCompany({ ...company, address: { ...company?.address, longitude: target.value } })}
                            InputLabelProps={{
                                shrink: !!company?.address?.longitude
                            }}
                        />
                    </Grid>

                    <Box sx={{ width: '100%', height: '1px', backgroundColor: '#ccc', margin: '10px' }} />

                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <CitySearchSelect
                                value={company?.coveredCities || []}
                                onChange={(value) => handleCityChange(value)}
                                onStarCity={handleStarClick}
                            />

                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <TextField
                            id="maps_zoom_field"
                            className={classes.itemInput}
                            type='number'
                            label="Zoom" size="small" color="primary" variant="outlined"
                            value={company?.mapsZoom}
                            onChange={({ target }) => setCompany({ ...company, mapsZoom: Number(target.value) })}
                            inputProps={{
                                type: 'number',
                                step: .1,
                                min: 0,
                                max: 12
                            }}
                            defaultValue={4}
                        />
                    </Grid>
                </Grid>

                <Divider size={2} />

                <DialogActions>
                    <Button
                        id="cancel_company_button"
                        onClick={handleCloseCompanyDialog}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        id="save_company_button"
                        onClick={saveCompany}
                        color="primary"
                        autoFocus
                        variant="contained"
                        disabled={loadingCompany}
                        endIcon={loadingCompany && (
                            <CircularProgress size={20} color="inherit" />
                        )}
                    >
                        {company.uuid ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showSelection || loadingReportPdf}
                onClose={() => !loadingReportPdf && setShowSelection(false)}
            >
                {loadingReportPdf ? (
                    <Grid container direction="column" alignItems="center" style={{ padding: 40, gap: 16 }}>
                        <CircularProgress size={32} />
                        <Typography>Gerando relatório, aguarde...</Typography>
                    </Grid>
                ) : (
                    <>
                        <DialogTitle>Período do Relatório</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Selecione um intervalo de no máximo 8 dias.
                            </Typography>
                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={ptBR}
                            >
                                <Box
                                    mt={2}
                                    display="flex"
                                    flexDirection="row"
                                    gap={2}
                                    sx={{ backgroundColor: 'transparent' }}
                                >
                                    <DatePicker
                                        label="Início"
                                        value={dateRange.start}
                                        onChange={(newValue) => newValue && setDateRange(p => ({ ...p, start: newValue }))}
                                        maxDate={subDays(new Date(), 1)}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    '& .MuiPaper-root': {
                                                        backgroundColor: '#ffffff'
                                                    }
                                                }
                                            },
                                            textField: {
                                                sx: { backgroundColor: '#ffffff' }
                                            }
                                        }}
                                    />
                                    <DatePicker
                                        label="Fim"
                                        value={dateRange.end}
                                        onChange={(newValue) => newValue && setDateRange(p => ({ ...p, end: newValue }))}
                                        minDate={dateRange.start}
                                        maxDate={subDays(new Date(), 1)}
                                        slotProps={{
                                            popper: {
                                                sx: {
                                                    '& .MuiPaper-root': {
                                                        backgroundColor: '#ffffff'
                                                    }
                                                }
                                            },
                                            textField: {
                                                sx: { backgroundColor: '#ffffff' }
                                            }
                                        }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowSelection(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={executeReportGeneration}>
                                Gerar Relatório
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={openedIcecastDialog} onClose={() => setOpenedIcecastDialog(false)}>
                <DialogTitle className={classes.dialogTitle}>
                    {!icecast?.uuid ? 'Cadastrando dados do ' : 'Editando dados do '} Icecast
                </DialogTitle>
                <Grid container className={classes.companyDialogContainer}>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="stream_server_url_field"
                            className={classes.itemInput}
                            label="Stream Server URL"
                            value={icecast?.streamServerURL}
                            onChange={({ target }) => setIcecast({ ...icecast, streamServerURL: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="stream_server_port_field"
                            className={classes.itemInput}
                            label="Stream Server Port"
                            value={icecast?.streamServerPort}
                            onChange={({ target }) => setIcecast({ ...icecast, streamServerPort: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="admin_username_field"
                            className={classes.itemInput}
                            label="Admin Username"
                            value={icecast?.adminUsername}
                            onChange={({ target }) => setIcecast({ ...icecast, adminUsername: target.value })}
                            size="small"
                            variant="outlined"
                            inputProps={{
                                'data-lpignore': 'true',
                                'data-1password': 'true',
                                'data-op-ignore': 'true'
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <PasswordField
                            value={icecast?.adminPassword}
                            onChange={({ target }) => setIcecast({ ...icecast, adminPassword: target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <TextField
                            id="mountpoint_name_field"
                            className={classes.itemInput}
                            label="Mountpoint Name"
                            value={icecast?.mountpointName}
                            onChange={({ target }) => setIcecast({ ...icecast, mountpointName: target.value })}
                            size="small"
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'
                        >
                            <InputLabel id="streaming_partner_label">Parceiro de streaming</InputLabel>
                            <Select
                                id="streaming_partner_field"
                                value={icecast?.streamingPartner}
                                onChange={({ target }) => setIcecast({ ...icecast, streamingPartner: target.value as StreamingPartner })}
                                variant="outlined"
                                color="primary"
                                label="Parceiro de streaming"
                                labelId="streaming_partner_label"
                            >
                                {streamingPartners.map((partner) => (
                                    <MenuItem key={partner.value} value={partner.value}>{partner.label}</MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_bitrate_label">Bitrate</InputLabel>
                            <Select
                                id="icecast_bitrate_field"
                                value={icecast?.currentBitrate ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentBitrate: Number(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Bitrate"
                                labelId="icecast_bitrate_label"
                            >
                                <MenuItem key={'32'} value={'32'}>32k</MenuItem>
                                <MenuItem key={'40'} value={'40'}>40k</MenuItem>
                                <MenuItem key={'48'} value={'48'}>48k</MenuItem>
                                <MenuItem key={'56'} value={'56'}>56k</MenuItem>
                                <MenuItem key={'64'} value={'64'}>64k</MenuItem>
                                <MenuItem key={'80'} value={'80'}>80k</MenuItem>
                                <MenuItem key={'96'} value={'96'}>96k</MenuItem>
                                <MenuItem key={'112'} value={'112'}>112k</MenuItem>
                                <MenuItem key={'128'} value={'128'}>128k</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_audio_format_label">Formato do áudio</InputLabel>
                            <Select
                                id="icecast_audio_format_field"
                                value={icecast?.currentFormat ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentFormat: String(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Formato do áudio"
                                labelId="icecast_audio_format_label"
                            >
                                <MenuItem key={'aac_low'} value={'aac_low'}>AAC LOW/LC</MenuItem>
                                <MenuItem key={'aac_he'} value={'aac_he'}>AAC HE</MenuItem>
                                <MenuItem key={'aac_he_v2'} value={'aac_he_v2'}>AAC HE V2</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                        <FormControl variant="outlined" className={classes.formControl} size='small'>
                            <InputLabel id="icecast_audio_frequency_label">Frequência do áudio</InputLabel>
                            <Select
                                id="icecast_audio_frequency_field"
                                value={icecast?.currentFrequency ?? ''}
                                onChange={({ target }) => setIcecast({ ...icecast, currentFrequency: Number(target.value) })}
                                variant="outlined"
                                color="primary"
                                label="Frequência do áudio"
                                labelId="icecast_audio_frequency_label"
                            >
                                <MenuItem key={'44100'} value={'44100'}>44100 Hz</MenuItem>
                                <MenuItem key={'48000'} value={'48000'}>48000 Hz</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                        <Button
                            id="fetch_audio_info_button"
                            onClick={fetchAudioInfo}
                            color="primary"
                            variant="outlined"
                            fullWidth
                            disabled={loadingAudioInfo || !icecast?.streamId}
                            startIcon={loadingAudioInfo && <CircularProgress size={16} />}
                        >
                            {loadingAudioInfo ? 'Buscando dados...' : 'Buscar dados do Icecast'}
                        </Button>
                    </Grid>
                </Grid>
                <Divider size={2} />
                <Grid container justifyContent="flex-end" alignItems="center" spacing={2} >
                    <Grid item>
                        <Button
                            id="icecast_dialog_cancel_button"
                            onClick={() => setOpenedIcecastDialog(false)}
                            color="primary"
                        >
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item>
                        <DialogActions>
                            <Button
                                id="icecast_dialog_save_button"
                                onClick={saveOrUpdateIcecast}
                                color="primary"
                                autoFocus
                                variant="contained"
                                disabled={loadingCompany}
                                endIcon={loadingCompany && (
                                    <CircularProgress size={20} color="inherit" />
                                )}
                            >
                                {icecast?.uuid ? 'Salvar Alterações' : 'Cadastrar'}
                            </Button>
                        </DialogActions>
                    </Grid>
                </Grid>
            </Dialog>


            <Dialog open={openedAudioInfoDialog} onClose={() => setOpenedAudioInfoDialog(false)}>
                <DialogTitle className={classes.dialogTitle}>
                    Dados detectados do stream
                </DialogTitle>
                <Grid container className={classes.companyDialogContainer}>
                    {detectedAudioInfo && (
                        <>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Formato detectado"
                                    value={
                                        detectedAudioInfo.format ?
                                            (isValidFormat(detectedAudioInfo.format)
                                                ? (formatMapping[detectedAudioInfo.format as keyof typeof formatMapping] || detectedAudioInfo.format)
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Bitrate detectado"
                                    value={
                                        detectedAudioInfo.bitrate ?
                                            (isValidBitrate(detectedAudioInfo.bitrate)
                                                ? `${detectedAudioInfo.bitrate}k`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Bitrate detectado (FFmpeg)"
                                    value={
                                        detectedAudioInfo.ffmpegBitrate ?
                                            (isValidBitrate(detectedAudioInfo.ffmpegBitrate)
                                                ? `${detectedAudioInfo.ffmpegBitrate}k`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogItemInput}>
                                <TextField
                                    label="Frequência detectada"
                                    value={
                                        detectedAudioInfo.frequency ?
                                            (isValidFrequency(detectedAudioInfo.frequency)
                                                ? `${detectedAudioInfo.frequency} Hz`
                                                : 'Desconhecido (incompatível)'
                                            ) : 'Desconhecido'
                                    }
                                    size="small"
                                    variant="outlined"
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    className={classes.itemInput}
                                />
                            </Grid>

                        </>
                    )}
                </Grid>
                <DialogActions>
                    <Button
                        onClick={() => setOpenedAudioInfoDialog(false)}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={applyDetectedAudioInfo}
                        color="primary"
                        variant="contained"
                    >
                        Aplicar dados compatíveis
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openedCompanyDelete}>
                <DialogTitle className={classes.dialogTitle}>
                    Tem certeza que deseja remover <b>{companyName}</b>?
                </DialogTitle>

                <DialogActions>
                    <Button
                        id="company_delete_cancel_button"
                        onClick={() => { setOpenedCompanyDelete(false) }}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        id="company_delete_confirm_button"
                        onClick={() => deleteCompany(companyUuid)}
                        color="primary"
                        autoFocus
                        variant="contained"
                        endIcon={deletingCompany && (
                            <CircularProgress size={20} color="inherit" />
                        )}
                    >
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Companies
